import { AxiosResponse } from "axios";
import BaseAPI from "../api";

const path = "invoice";

export interface InvoiceInterface {
  page?: number;
  pageLimit?: number;
  dateStart?: string;
  userId?:any;
  status?:any;
}

interface graphInterface {
  dateStart: string
  dateEnd: string
  branchId: number
  leasesTypeId: number
}

interface InvoicePayInterface {
  branchId: number,
  invoiceId: number,
  invoiceTypeId: number,
  invoicePayDate: string,
  invoicePayAmount: number
}
interface invoiceDetailType {
  sequential: number,
  paymentServiceId: number,
  description: string,
  amount: number;
}
interface invoiceTransferType {
  branchId: number,
  invoiceDateStart: string | Date,
  invoiceDateEnd: string | Date,
  leaseAgreementId: number,
  leaseAgreementTypeId: number,
  zoneId: number,
  stallId: number,
  status: string,
  invoiceDetail: invoiceDetailType[]
}

/** sortBy ที่ backend รองรับ — ค่านอกลิสต์จะ fallback ไป notiMailCount (ไม่ error) */
export const INVOICE_NOTICE_SORT_BY = [
  "notiMailCount", "overdueDays", "invoiceId", "invoiceNo", "invoiceDate",
  "invoiceDateEnd", "periodDate", "createDate", "zoneName", "stallNumber",
  "stallName", "leasesTypeNameTh",
] as const;
export type InvoiceNoticeSortBy = typeof INVOICE_NOTICE_SORT_BY[number];

export interface InvoiceNoticeParams {
  keyword?: string;
  userId?: number;   // radio ผู้เช่า
  zoneId?: number;   // radio ทั้งโซน
  stallId?: number;  // radio รายแผงค้า
  branchId?: number;
  leaseAgreementTypeId?: number;
  periodDate?: string;
  start_date?: string;
  end_date?: string;
  /** ส่งได้เฉพาะ NOT_PAY | WAITING — ค่าอื่นจะ AND กับตัวกรองค้างชำระ ได้ผลว่างเปล่าไม่ใช่ error */
  status?: string;
  sortBy?: InvoiceNoticeSortBy;
  sortType?: "ASC" | "DESC";
  page?: number;
  /** ⚠️ ชื่อ `limit` ไม่ใช่ `pageLimit` เหมือน endpoint อื่นในไฟล์นี้ */
  limit?: number;
}

/** ต้องส่งค่าทุกตัวเดียวกับที่ใช้ยิง GET /invoice/notice ของตารางเป๊ะ */
export interface InvoiceNoticeFilter {
  keyword?: string;
  userId?: number;
  zoneId?: number;
  stallId?: number;
  branchId?: number;
  leaseAgreementTypeId?: number;
  periodDate?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
}

export interface InvoiceNoticePayload {
  /** true = ส่งทุกบิลที่ตรง filter ทุกหน้า | false = ใช้ includes */
  selectAll?: boolean;
  includes?: number[];
  excludes?: number[];
  /** ⚠️ บังคับเมื่อ selectAll = true — ส่งไม่ครบ (เช่นลืม zoneId) = ยิงเมลทั้งระบบ */
  filter?: InvoiceNoticeFilter;
  remark?: string;
}

export interface NoticeTenantOption {
  userId: number;
  fullname?: string;
  firstname?: string;
  lastname?: string;
  email?: string | null;
  invoiceCount?: number;
  totalAmount?: number;
  /** ยังไม่มีใน contract ปัจจุบัน — รอ backend เพิ่ม ถ้าไม่มีจะไม่แสดง */
  idCard?: string;
}

export interface NoticeZoneOption {
  zoneId: number;
  zoneName?: string;
  invoiceCount?: number;
  totalAmount?: number;
}

export interface NoticeStallOption {
  stallId: number;
  zoneId?: number;
  stallNumber?: string;
  stallName?: string;
  /** label ที่ backend ประกอบมาให้แล้ว: `เลขที่แผง - ชื่อแผง` */
  stallLabel?: string;
  invoiceCount?: number;
  totalAmount?: number;
}

export default class InvoiceApi extends BaseAPI {
  static findAll(params: InvoiceInterface,timeout?:number): Promise<any> {
    return this.api.get(path, { params: { ...params } ,timeout:timeout}).then((res) => res);
  }
  static findAllNew(params: InvoiceInterface): Promise<any> {
    return this.api.get(`${path}/new`, { params: { ...params } }).then((res) => res);
  }
  /** ใบแจ้งหนี้เฉพาะที่มีรายการ "ค่าโอนแผงค้า" — param/response เหมือน findAllNew ทุกอย่าง ต่างแค่ where ฝั่ง backend */
  static findAllTransferFee(params: InvoiceInterface): Promise<any> {
    return this.api.get(`${path}/new/transfer-fee`, { params: { ...params } }).then((res) => res);
  }
  /** รายการบิลค้างชำระสำหรับหน้าแจ้งเตือน — backend บังคับ status IN (NOT_PAY, WAITING) ให้เสมอ */
  static findAllNotice(params: InvoiceNoticeParams): Promise<any> {
    return this.api.get(`${path}/notice`, { params: { ...params } }).then((res) => res);
  }
  /**
   * ยิงอีเมลแจ้งเตือน — **1 อีเมล = 1 ผู้เช่า** (ไม่ใช่ 1 บิล) พร้อมแนบ PDF หนังสือทวงหนี้ 2 ไฟล์
   * timeout ยาวเป็นพิเศษเพราะแต่ละฉบับต้อง render PDF + ยิง API รายงาน .NET อีก 1 รอบ แบบ sequential
   * ตั้งสั้นไปยิ่งอันตราย — ถ้า client ตัดแต่ server ส่งไปแล้ว ผู้ใช้กดซ้ำ = ผู้เช่าได้เมล 2 ฉบับ
   */
  static sendNotice(payload: InvoiceNoticePayload): Promise<any> {
    return this.api.post(`${path}/notice`, payload, { timeout: 180000 }).then((res) => res);
  }
  /** dropdown ผู้เช่า — เฉพาะคนที่มีบิลค้างชำระจริง พร้อม invoiceCount/totalAmount */
  static findNoticeTenants(params: { keyword?: string; zoneId?: number; stallId?: number; branchId?: number }): Promise<any> {
    return this.api.get(`${path}/notice/tenants`, { params: { ...params } }).then((res) => res);
  }
  /** dropdown โซน — เฉพาะโซนที่มีบิลค้างชำระจริง */
  static findNoticeZones(params: { keyword?: string; branchId?: number; userId?: number }): Promise<any> {
    return this.api.get(`${path}/notice/zones`, { params: { ...params } }).then((res) => res);
  }
  /** dropdown แผงค้า — ต้องส่ง zoneId ตาม flow เลือกโซนก่อน */
  static findNoticeStalls(params: { zoneId?: number; keyword?: string; branchId?: number; userId?: number }): Promise<any> {
    return this.api.get(`${path}/notice/stalls`, { params: { ...params } }).then((res) => res);
  }
  static creatInvoice(props: any): Promise<any> {
    return this.api.post(path, props).then((res) => res);
  }
  static transferInvoice(props: invoiceTransferType): Promise<any> {
    return this.api.post(`${path}/tranfer`, props).then(res => res);
  }

  static findById(id: number): Promise<any> {
    return this.api.get(`${path}/${id}`).then((res) => res);
  }
  static overdue(params: any): Promise<any> {
    return this.api.get(`${path}/overdue`, { params: { ...params } }).then((res) => res);
  }

  static updateAll(props: any): Promise<any> {
    return this.api.patch(`${path}/status`, props).then((res) => res);
  }

  static updateById(props: any, id: number): Promise<any> {
    return this.api.patch(`${path}/${id}`, props)
  }

  static detail(id: any): Promise<any> {
    return this.api.get(`${path}/${id}`, id).then((res) => res);
  }

  static graph(params: graphInterface): Promise<any> {
    return this.api.get(`${path}/calculation`, { params: { ...params } }).then((res) => res);
  }

  static bank(id: number): Promise<any> {
    return this.api.get(`${path}/${id}/bank`, { responseType: 'arraybuffer' });
  }

  static pay(payload: InvoicePayInterface): Promise<any> {
    return this.api.post(`${path}/pay`, payload);
  }

  static updateStatus(id: number, payload: any): Promise<any> {
    return this.api.patch(`${path}/${id}/status`, { status: payload })
  }
  static updateApprove(id: number, payload: any): Promise<any> {
    return this.api.patch(`${path}/${id}/approve`, payload)
  }

  static updateStatusNotApprove(id: number, payload: any): Promise<any> {
    return this.api.patch(`${path}/${id}/status`, payload)
  }

  static receipt(id: number, type: number = 1): Promise<any> {
    return this.api.get(`${path}/${id}/receipt?type=${type}`, { responseType: 'arraybuffer' })
  }


  // delete
  static deleted(id:number):Promise<AxiosResponse>{
    return this.api.delete(`${path}/${id}`)
  }
}